import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Circle, ChevronRight, X, User, Map,
  ShoppingBag, ShoppingCart, Package, Users, Award, ChevronDown
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cop31_token')}` } });

const STEPS = [
  {
    id: 'profile_complete',
    icon: User,
    titleTr: 'Profilinizi Tamamlayın',
    titleEn: 'Complete Your Profile',
    descTr: 'Ad, şirket ve iletişim bilgilerinizi doldurun',
    descEn: 'Fill in your name, company and contact details',
    tab: 'profile',
  },
  {
    id: 'layout_viewed',
    icon: Map,
    titleTr: 'Stand Düzeninizi Görüntüleyin',
    titleEn: 'View Your Stand Layout',
    descTr: 'Size atanan stand planını inceleyin',
    descEn: 'Review the stand plan assigned to you',
    tab: 'layout',
  },
  {
    id: 'products_explored',
    icon: Package,
    titleTr: 'Ürün Kataloğunu Keşfedin',
    titleEn: 'Explore the Product Catalog',
    descTr: 'Tedarikçi ürünlerine göz atın',
    descEn: 'Browse supplier products and services',
    tab: 'products',
  },
  {
    id: 'cart_added',
    icon: ShoppingCart,
    titleTr: 'Sepete Ürün Ekleyin',
    titleEn: 'Add a Product to Cart',
    descTr: 'Beğendiğiniz ürünü sepete ekleyin',
    descEn: 'Add a product you like to your cart',
    tab: 'products',
  },
  {
    id: 'first_order',
    icon: ShoppingBag,
    titleTr: 'İlk Siparişinizi Oluşturun',
    titleEn: 'Place Your First Order',
    descTr: 'Sipariş veya teklif talebi gönderin',
    descEn: 'Submit an order or request a quote',
    tab: 'orders',
  },
  {
    id: 'user_invited',
    icon: Users,
    titleTr: 'Çalışan Davet Edin',
    titleEn: 'Invite a Team Member',
    descTr: 'Kullanıcılar sekmesinden ekibinizi davet edin',
    descEn: 'Invite your team from the Users tab',
    tab: 'users',
  },
];

const CustomerOnboarding = ({ tr, onNavigate, onStepComplete, onOpenWizard }) => {
  const [completed, setCompleted] = useState([]);
  const [percent, setPercent] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchProgress = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/customer/onboarding`, getAuth());
      setCompleted(r.data.completed_steps || []);
      setPercent(r.data.percent || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  // Expose refresh so parent can call after actions
  useEffect(() => {
    if (onStepComplete) onStepComplete.current = fetchProgress;
  }, [onStepComplete, fetchProgress]);

  const completeStep = async (stepId) => {
    if (completed.includes(stepId)) return;
    try {
      const r = await axios.post(`${API}/customer/onboarding/complete-step`, { step_id: stepId }, getAuth());
      setCompleted(r.data.completed_steps || []);
      setPercent(r.data.percent || 0);
    } catch { /* ignore */ }
  };

  const handleStepClick = (step) => {
    // profile_complete adımı tamamlanmamışsa → wizard'ı aç (kaldığı yerden)
    if (step.id === 'profile_complete' && !completed.includes(step.id)) {
      if (onOpenWizard) onOpenWizard();
      setOpen(false);
      return;
    }
    if (onNavigate) onNavigate(step.tab);
    setOpen(false);
  };

  if (loading || dismissed || percent === 100) {
    // Show a small "completed" badge for a moment then hide
    if (percent === 100 && !dismissed) {
      return (
        <div className="fixed left-4 bottom-6 z-50">
          <div className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-2xl shadow-xl text-sm font-semibold">
            <Award className="h-4 w-4" />
            {tr ? 'Onboarding Tamamlandı!' : 'Onboarding Complete!'}
            <button onClick={() => setDismissed(true)} className="ml-1 opacity-70 hover:opacity-100">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      );
    }
    return null;
  }

  const done = completed.length;
  const total = STEPS.length;

  // Circle progress math
  const r = 18;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;

  return (
    <>
      {/* ── Trigger button (always visible on left) ── */}
      <div className="fixed left-4 bottom-6 z-50">
        <button
          onClick={() => setOpen(o => !o)}
          className="group relative flex items-center gap-2.5 bg-white border-2 border-blue-200 hover:border-blue-400 shadow-lg hover:shadow-blue-100 rounded-2xl px-3.5 py-2.5 transition-all"
        >
        {/* Mini circular progress */}
        <div className="relative flex-shrink-0" style={{width: 36, height: 36}}>
          <svg width="36" height="36" viewBox="0 0 36 36" style={{transform: 'rotate(-90deg)'}}>
            <circle cx="18" cy="18" r={r} fill="none" stroke="#dbeafe" strokeWidth="3.5" />
            <circle
              cx="18" cy="18" r={r} fill="none"
              stroke="#3b82f6" strokeWidth="3.5"
              strokeDasharray={circ}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
          </svg>
          <span style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '9px', fontWeight: 700, color: '#2563eb', lineHeight: 1
          }}>{percent}%</span>
        </div>
          <div className="text-left">
            <p className="text-xs font-bold text-blue-700 leading-tight">{tr ? 'Başlangıç' : 'Onboarding'}</p>
            <p className="text-xs text-blue-400">{done}/{total} {tr ? 'adım' : 'steps'}</p>
          </div>
          <ChevronDown className={`h-3.5 w-3.5 text-blue-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* ── Slide-up panel ── */}
      {open && (
        <div className="fixed left-4 bottom-24 z-50 w-80 bg-white rounded-3xl shadow-2xl border border-blue-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-white text-sm">{tr ? 'Başlangıç Rehberi' : 'Getting Started'}</h3>
                <p className="text-blue-200 text-xs mt-0.5">{tr ? `${done}/${total} adım tamamlandı` : `${done}/${total} steps completed`}</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Progress bar */}
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="text-white/70 text-xs mt-1.5 text-right font-mono">%{percent}</p>
          </div>

          {/* Steps */}
          <div className="p-3 space-y-1.5 max-h-80 overflow-y-auto">
            {STEPS.map((step, i) => {
              const isDone = completed.includes(step.id);
              const Icon = step.icon;
              return (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(step)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group ${
                    isDone
                      ? 'bg-blue-50 opacity-70'
                      : 'hover:bg-blue-50 border border-transparent hover:border-blue-100'
                  }`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    isDone ? 'bg-blue-500' : 'bg-gray-100 group-hover:bg-blue-100'
                  }`}>
                    {isDone
                      ? <CheckCircle className="h-4 w-4 text-white" />
                      : <Icon className="h-4 w-4 text-gray-500 group-hover:text-blue-600" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold leading-tight ${isDone ? 'text-blue-700 line-through' : 'text-gray-800'}`}>
                      {tr ? step.titleTr : step.titleEn}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-snug">{tr ? step.descTr : step.descEn}</p>
                  </div>
                  {!isDone && <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 text-center">
            <p className="text-xs text-gray-400">
              {tr ? 'Her adıma tıklayarak ilgili sayfaya gidin' : 'Click each step to navigate to the relevant page'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerOnboarding;
export { STEPS };
